import React, { useCallback, useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Chip, Box, Button, IconButton, Tooltip } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorContainer } from "../../app/ErrorContainer";
import { LoadingContainer } from "../../app/LoadingContainer";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { CompareOffersRequest, loadComparisionResults, UrsOfferComparisonResult } from "./UrsOfferComparatorApi";
import { unwrapResult } from "@reduxjs/toolkit";
import { NoteLabel } from "../ursOffer/NoteLabel";
import { RequirementNote } from "../ursOffer/ursOfferSlice";
import PrintIcon from '@mui/icons-material/Print';
import { selectSettings } from "../settings/settingsSlice";
import { Localized } from "@fluent/react";
import CloseIcon from '@mui/icons-material/Close';

export const UrsOfferComparator = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate()

    const selectedOfferIds = location.state?.selectedOfferIds || [];
    const ursId = location.state?.ursId || 0;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [error, setError] = useState(null);
    const [compareResults, setCompareResults] = useState<UrsOfferComparisonResult | null>(null);
    const { locale } = useAppSelector(selectSettings);

    const [activeSuppliers, setActiveSuppliers] = useState<{ id: number; offerId: number; active: boolean, name: string }[]>([]);

    useEffect(() => {
        setIsLoading(true);
        const request: CompareOffersRequest = { ursId, offerIds: selectedOfferIds };

        dispatch(loadComparisionResults(request))
            .then(unwrapResult)
            .then((result) => {
                setCompareResults(result);
                setActiveSuppliers(result.suppliers.map(s => ({ id: s.id, offerId: s.offerId, active: true, name: s.name })));
            })
            .catch((error) => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch, ursId, selectedOfferIds]);


    const handleBackButtonClick = useCallback(() => {
        navigate(-1)
    }, [navigate])

    if (isError)
        return <ErrorContainer error={error} />;

    if (isLoading)

        return <LoadingContainer />;

    if (!compareResults)
        return <Typography variant="h6"><Localized id="urs-no-comparision-result ">Brak wyników porównania</Localized> </Typography>;

    const toggleSupplierVisibility = (supplierId: number, offerId: number) => {
        setActiveSuppliers((prev) =>
            prev.map(s =>
                s.id === supplierId && s.offerId === offerId ? { ...s, active: !s.active } : s
            )
        );
    };

    const handlePrint = async () => {
        const offerIdsParam = activeSuppliers.filter(s => s.active).map(x => `offerIds=${x.offerId}`).join("&");
        if (offerIdsParam) {
            const url = `${process.env.REACT_APP_REST_API}api/urs-offer-comparator/report/${ursId}?language=${locale}&${offerIdsParam}`;

            window.open(url, '_blank');
        }

    };

    return (
        <>
            <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        backgroundColor: 'white',
                        padding: 2,
                        borderBottom: '1px solid #ddd',
                        boxShadow: 1,
                        position: 'relative'
                    }}
                >
                    <Tooltip title={<Localized id='close'>_close</Localized>}>
                        <IconButton
                            onClick={handleBackButtonClick}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                border: '1px solid #ccc',
                                backgroundColor: '#f9f9f9',
                                '&:hover': {
                                    backgroundColor: '#eee',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>

                    <Typography variant="h6" gutterBottom>
                        <Localized id="urs-selected-offers-to-compare">_compare</Localized> (
                        {activeSuppliers.filter((s) => s.active).length})
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        <Localized id="urs-selected-offers-compare-description">_compare</Localized>
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {activeSuppliers.map((supplier) => (
                                <Chip
                                    key={`${supplier.id}-${supplier.offerId}`}
                                    label={supplier.name}
                                    onClick={() => toggleSupplierVisibility(supplier.id, supplier.offerId)}
                                    sx={{
                                        backgroundColor: supplier.active ? 'green' : '#ccc',
                                        color: supplier.active ? 'white' : 'black',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                    }}
                                />
                            ))}
                        </Box>

                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            <Localized id="print-compare">_Drukuj porownanie</Localized>
                        </Button>
                    </Box>
                </Box>

                <TableContainer component={Paper} sx={{ mt: 1, p: 2, flexGrow: 1, overflow: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={2} />
                                {compareResults.suppliers
                                    .filter(supplier => activeSuppliers.some(vs => vs.id === supplier.id && vs.offerId === supplier.offerId && vs.active))
                                    .map((supplier, index) => (
                                        <TableCell
                                            key={`${supplier.id}-${supplier.offerId}`}
                                            colSpan={2}
                                            align="center"
                                            sx={{
                                                fontWeight: "bold",
                                                backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white",
                                                borderLeft: index > 0 ? "2px solid #ddd" : "none",
                                            }}
                                        >
                                            {supplier.name}
                                        </TableCell>
                                    ))}
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <Localized id="urs-requirement-no">_No</Localized>
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <Localized id="urs-offer-requirement">_requirement</Localized>
                                </TableCell>

                                {compareResults.suppliers
                                    .filter(supplier => activeSuppliers.some(vs => vs.id === supplier.id && vs.offerId === supplier.offerId && vs.active))
                                    .map((supplier, index) => (
                                        <React.Fragment key={`${supplier.id}-${supplier.offerId}`}>
                                            <TableCell
                                                sx={{
                                                    fontWeight: "bold",
                                                    backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white",
                                                    borderLeft: index > 0 ? "2px solid #ddd" : "none",
                                                }}
                                            >
                                                <Localized id="urs-offer-note">_Ocena</Localized>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: "bold",
                                                    backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white",
                                                }}
                                            >
                                                <Localized id="urs-offer-comment">_Komentarz</Localized>
                                            </TableCell>
                                        </React.Fragment>
                                    ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {compareResults.requirements.map((req) => (
                                <TableRow
                                    key={req.id}
                                    hover
                                >
                                    <TableCell>{req.numeration}</TableCell>
                                    <TableCell> <div dangerouslySetInnerHTML={{ __html: req.name }}></div></TableCell>

                                    {compareResults.suppliers
                                        .filter(supplier => activeSuppliers.some(vs => vs.id === supplier.id && vs.offerId === supplier.offerId && vs.active))
                                        .map((supplier, index) => (
                                            <React.Fragment key={`${supplier.id}-${supplier.offerId}-${req.id}`}>
                                                <TableCell
                                                    sx={{
                                                        borderLeft: index > 0 ? "2px solid #ddd" : "none",
                                                        backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white",
                                                    }}
                                                >
                                                    <NoteLabel note={req.notes[index] as RequirementNote | null} />
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white",
                                                    }}
                                                >
                                                    <Typography variant="caption">
                                                        {req.comments[index] ?? "-"}
                                                    </Typography>
                                                </TableCell>
                                            </React.Fragment>
                                        ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};
